import { LocalStorageConfig } from '../constants/Constants';

/** What the global fleet dashboard remembers between visits. */
export interface FleetDashboardState {
  /** The row the user picked: drives the map zoom, the row highlight and the device list. */
  facility?: { facilityId: string; facilityName: string; stationIataCode?: string } | null;
  /** The composed filter array the facility table was last fetched with. */
  filters?: any[];
  equipmentType?: string;
  facilityType?: string;
  pagination?: { pageIndex: number; pageSize: number };
}

export const Storage = {
  getSearchBarFiltering(key, removeIfTmp?: boolean): any {
    let retVal = null;

    const storageKey = `${LocalStorageConfig.SEARCHBAR_FILTERS}_${key}`.toLowerCase();
    const filters = localStorage.getItem(storageKey);
    if (filters) {
      const filterStates = JSON.parse(filters);
      if (filterStates) {
        retVal = filterStates.filter;
      }
    }

    // if this is a temporary filter (for example, a redirect from another page, searching for a particular id),
    // apply the filter and remove it after 500 milliseconds, so that eventual
    // rerenders also still retrieve this temporary filter
    if (retVal && removeIfTmp && storageKey.endsWith('_tmp')) setTimeout(() => localStorage.removeItem(storageKey), 500);

    return retVal;
  },

  clearSearchBarFiltering(key) {
    const storageKey = `${LocalStorageConfig.SEARCHBAR_FILTERS}_${key}`.toLowerCase();
    localStorage.removeItem(storageKey);
  },
  setSearchBarFiltering(key, filter) {
    if (!filter) return;
    const storageKey = `${LocalStorageConfig.SEARCHBAR_FILTERS}_${key}`.toLowerCase();
    const filters = localStorage.getItem(storageKey);
    if (filters) {
      const existFilter = JSON.parse(filters);
      if (existFilter) {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            key,
            filter: {
              value: filter.isMoreFilter ? existFilter.filter?.value : filter.isSearchBar ? filter.value : undefined,
              column: filter.isMoreFilter ? existFilter.filter?.column : filter.isSearchBar ? filter.column : undefined,
              filters: filter.isMoreFilter ? filter.filters : existFilter.filter?.filters,
            },
          }),
        );
        return;
      }
    }
    localStorage.setItem(storageKey, JSON.stringify({ key, filter }));
  },

  getFleetDashboardState(): FleetDashboardState | null {
    const raw = localStorage.getItem(LocalStorageConfig.FLEET_DASHBOARD_STATE);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as FleetDashboardState) : null;
    } catch {
      // Unparseable value (hand-edited, or written by an older build): start clean.
      return null;
    }
  },

  // Merges, so each caller can save just the slice it owns.
  setFleetDashboardState(patch: Partial<FleetDashboardState>) {
    const next = { ...(Storage.getFleetDashboardState() ?? {}), ...patch };
    localStorage.setItem(LocalStorageConfig.FLEET_DASHBOARD_STATE, JSON.stringify(next));
  },

  clearFleetDashboardState() {
    localStorage.removeItem(LocalStorageConfig.FLEET_DASHBOARD_STATE);
  },

  // Remembers the last device viewed under /visits/:id, so returning to the
  // bare /visits screen (e.g. after navigating away and back) can resume there.
  getLastVisitId(): { id: string; bySerial: boolean } | null {
    const raw = localStorage.getItem(LocalStorageConfig.LAST_VISIT_ID);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.id) {
        return { id: parsed.id, bySerial: !!parsed.bySerial };
      }
    } catch {
      // fall through — value predates this format (a plain id string)
    }

    // legacy plain-string value: treat as a deviceId, not a serial
    return { id: raw, bySerial: false };
  },
  setLastVisitId(id: string, bySerial = false) {
    localStorage.setItem(LocalStorageConfig.LAST_VISIT_ID, JSON.stringify({ id, bySerial }));
  },
  clearLastVisitId() {
    localStorage.removeItem(LocalStorageConfig.LAST_VISIT_ID);
  },
};

export async function clearCache() {
  window.localStorage.clear();
  window.sessionStorage.clear();

  const keys = await caches.keys();
  keys.forEach((key) => {
    caches.delete(key);
  });
}

export const getRetainState = (key, stateName, setValue, isDateRang = false, filterType = '') => {
  if (!stateName) return;

  const filterStages = Storage.getSearchBarFiltering(key);
  const filter = filterStages?.filters?.find((v) => v[stateName] !== undefined);
  if (filter && isDateRang) {
    if (filter.filterType === filterType) {
      setValue(filter[stateName]); // Date From
    } else {
      if (filter.and && filter.and.filterType === filterType) {
        setValue(filter.and[stateName]); // Date To
      }
    }
    return;
  }

  if (filter) {
    setValue(filter[stateName]);
  }
};
